import { compareAsc, compareDesc } from 'date-fns';
import { tryAddMoneySameCurrency } from 'src/utils/ticketMetricUtils';
import {
  ActionOutboxEntityType,
  EventSort,
  EventWithData,
  ListingDetailedMetrics,
  Tag,
} from 'src/WebApiController';

import { getEventsWithNonZeroCounts } from '../CatalogDataContext/CatalogDataContext.utils';
import {
  EventIdGroup,
  MAX_ENTITIES_IN_GROUP_FOR_LOAD,
} from './CatalogMetrics.type';

export const getEventIdsForPerformersOrVenues = (
  events?: Record<string, EventWithData>,
  performerIds?: number[] | null,
  venueEventIds?: number[] | null
) => {
  if (!events || !performerIds) {
    return [];
  }

  const results = Object.values(events)
    .filter(
      (ev) =>
        (ev.event.perfId && performerIds?.includes(ev.event.perfId)) ||
        (ev.event.venId && venueEventIds?.includes(ev.event.venId))
    )
    .map((ev) => ev.event.virtId);

  return results;
};

/* The goal of this method is to aggregate all the listing metrics with similar currency so we can reduce significantly the number of listing metrics
 * being sent to the server for aggregation just because of currency conversions
 * if the aggregation result in just 1 ListingMetrics - we don't need to send anything
 */
export const groupAndAggregateMetrics = <
  TMetrics extends { currency: string | null },
>(
  listingMetrics: TMetrics[],
  addMetrics: (metricA: TMetrics, metricB: TMetrics) => TMetrics
) => {
  const metricsByCurrency = {} as Record<string, TMetrics>;

  listingMetrics.forEach((lm) => {
    const existing = metricsByCurrency[lm.currency ?? ''];
    if (!existing) {
      metricsByCurrency[lm.currency ?? ''] = lm;
    } else {
      metricsByCurrency[lm.currency ?? ''] = addMetrics(existing, lm);
    }
  });

  return Object.values(metricsByCurrency);
};

export function getEventsToGetMetricsFor(
  data: EventWithData[],
  entityType: ActionOutboxEntityType,
  sortBy?: EventSort | null,
  isSortDescending?: boolean | null
): EventIdGroup[][] {
  const events = getEventsWithNonZeroCounts(entityType, data, true);

  const allEventIds = events
    .map(
      (ev) =>
        ({
          viagId: ev.event.viagId,
          viagVirtId: ev.event.virtId,
          performerId: ev.event.perfId,
          venueId: ev.event.venId,
          name: ev.event.name,
          date: ev.event.datesUtc,
        }) as EventIdGroup
    )
    .toSorted((ev1, ev2) => {
      if (sortBy === EventSort.MainDisplay) {
        return isSortDescending
          ? ev2.name.localeCompare(ev1.name)
          : ev1.name.localeCompare(ev2.name);
      }

      const date1 = ev1.date.start ? new Date(ev1.date.start) : new Date(0);
      const date2 = ev2.date.start ? new Date(ev2.date.start) : new Date(0);
      // Sort the events by dates
      // given 99% of the time the sort is date
      // If the sort is by name
      return isSortDescending
        ? compareDesc(date1, date2)
        : compareAsc(date1, date2);
    });

  if (allEventIds.length <= MAX_ENTITIES_IN_GROUP_FOR_LOAD) {
    return [allEventIds];
  }

  return [
    allEventIds.slice(0, MAX_ENTITIES_IN_GROUP_FOR_LOAD),
    allEventIds.slice(MAX_ENTITIES_IN_GROUP_FOR_LOAD),
  ];
}

export const createAggregatedListingDetailedMetrics = (
  allLds: ListingDetailedMetrics[] | null | undefined
): ListingDetailedMetrics | null => {
  if (!allLds?.length) {
    return null;
  }

  const firstMetric = allLds[0];

  // Check if all metrics have the same currency
  const currency = firstMetric.currency;

  // Aggregate quantities from base class properties
  const totalTktQty = allLds.reduce((sum, m) => sum + m.tktQty, 0);
  const totalSoldQty = allLds.reduce((sum, m) => sum + m.soldQty, 0);
  const totalUnsoldQty = allLds.reduce((sum, m) => sum + m.unsoldQty, 0);
  const totalListQty = allLds.reduce((sum, m) => sum + m.listQty, 0);
  const totalUnlistQty = allLds.reduce((sum, m) => sum + m.unlistQty, 0);
  const totalQtySoldL1 = allLds.reduce((sum, m) => sum + m.qtySoldL1, 0);
  const totalQtySoldL3 = allLds.reduce((sum, m) => sum + m.qtySoldL3, 0);
  const totalQtySoldL7 = allLds.reduce((sum, m) => sum + m.qtySoldL7, 0);
  const totalUnfulfilledTickets = allLds.reduce(
    (sum, m) => sum + m.unfulfilledTickets,
    0
  );
  const totalFulfillableTickets = allLds.reduce(
    (sum, m) => sum + m.fulfillableTickets,
    0
  );

  const totalCost = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.ttlCst,
    (ld) => ld.currency
  )?.amt;
  const totalSoldCost = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.soldCst,
    (ld) => ld.currency
  )?.amt;
  const totalUnsoldCost = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.ttlCst - ld.soldCst,
    (ld) => ld.currency
  )?.amt;
  const totalListPrice = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.ttlListPrc,
    (ld) => ld.currency
  )?.amt;
  const totalUnsoldListPrice = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.ttlUnsoldListPrc,
    (ld) => ld.currency
  )?.amt;
  const totalNetProceeds = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.netProcs,
    (ld) => ld.currency
  )?.amt;
  const totalPandL = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.pandL,
    (ld) => ld.currency
  )?.amt;
  const totalRevenue = tryAddMoneySameCurrency(
    allLds,
    (ld) => ld.rvn,
    (ld) => ld.currency
  )?.amt;

  // Calculate aggregated metrics
  const aggregatedSTR = totalTktQty > 0 ? totalSoldQty / totalTktQty : 0;
  const aggregatedLiftPerc =
    totalRevenue != null && totalRevenue > 0 && totalPandL != null
      ? totalPandL / totalRevenue
      : 0;
  const aggregatedATP =
    totalSoldQty > 0 && totalNetProceeds != null
      ? totalNetProceeds / totalSoldQty
      : 0;
  const aggregatedListedATP =
    totalListQty > 0
      ? allLds.reduce((sum, m) => sum + m.listedATP * m.listQty, 0) /
        totalListQty
      : 0;

  const aggregatedMetrics: ListingDetailedMetrics = {
    tktQty: totalTktQty,
    soldQty: totalSoldQty,
    unsoldQty: totalUnsoldQty,
    listQty: totalListQty,
    unlistQty: totalUnlistQty,
    currency: currency,
    ttlCst: totalCost ?? 0,
    soldCst: totalSoldCost ?? 0,
    unsoldCst: totalUnsoldCost ?? 0,
    ttlListPrc: totalListPrice ?? 0,
    ttlUnsoldListPrc: totalUnsoldListPrice ?? 0,
    netProcs: totalNetProceeds ?? 0,
    pandL: totalPandL ?? 0,
    str: aggregatedSTR,
    rvn: totalRevenue ?? 0,
    liftPerc: aggregatedLiftPerc,
    atp: aggregatedATP,
    listedATP: aggregatedListedATP,
    priceUpdatedOn: null,
    tags: [] as Tag[],
    lastSaleDate: null,
    qtySoldL1: totalQtySoldL1,
    qtySoldL3: totalQtySoldL3,
    qtySoldL7: totalQtySoldL7,
    unfulfilledTickets: totalUnfulfilledTickets,
    fulfillableTickets: totalFulfillableTickets,
    pendSrc: allLds.reduce((sum, m) => sum + (m.pendSrc ?? 0), 0),
  };

  return aggregatedMetrics;
};
